// packages/web/src/lib/sanitizeEmailHtml.ts — MAIL-POLISH BRIEF (2026-10-16,
// Huan's own words: "render the email properly (HTML, links, images)").
//
// ⚑ DOMPURIFY IS THE SANITIZER (atlas-core review round 2, 2026-10-16:
// "use DOMPurify, not a hand-rolled sanitizer, for untrusted email HTML").
// An earlier cut of this file walked the DOM by hand; that is gone. This file
// is now CONFIGURATION for a proven sanitizer, in three parts that stay in
// this file on purpose so the policy is readable in one place:
//   - ALLOWED_TAGS / ALLOWED_ATTR: what survives, and nothing else. Every
//     `on*` handler, `<script>`, `<style>`, `<iframe>`, `<object>`, `<embed>`,
//     `<form>` and its controls, `<link>`, `<base>`, `<meta>` is outside the
//     allow-list (and forbidden by name, belt beside suspenders), and
//     `javascript:` / `data:` URLs fail ALLOWED_URI_REGEXP (only http(s),
//     mailto, and `cid:` — the last only so the hook below can turn it into a
//     placeholder).
//   - ONE hook (`afterSanitizeAttributes`) that does the things a tag list
//     cannot: strips CSS `url(...)` from a `style` attribute ALWAYS (a
//     background-image is a tracking pixel by another name — never gated by
//     "Show images"); gives every link `target="_blank" rel="noopener
//     noreferrer"`; and neutralizes `<img>`: a `cid:` reference is ALWAYS a
//     placeholder (inline-attachment bytes are a separate, undelivered chunk),
//     a remote http(s) src is held in `data-blocked-src` with the real `src`
//     removed UNLESS `showImages` is true — the ONLY thing `showImages` gates.
//   - The sandboxed iframe (Mail.tsx's EmailBody: no allow-scripts, no
//     allow-same-origin) is the SECOND wall; this is the first.
// DOMPurify is an exact-pinned dependency of packages/web.
import createDOMPurify from 'dompurify';
import { useState } from 'preact/hooks';

const ALLOWED_TAGS = [
  'a', 'b', 'strong', 'i', 'em', 'u', 's', 'strike', 'p', 'br', 'hr', 'div', 'span',
  'ul', 'ol', 'li', 'table', 'thead', 'tbody', 'tfoot', 'tr', 'td', 'th', 'img',
  'blockquote', 'pre', 'code', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'font', 'center',
  'small', 'sub', 'sup', 'label',
];

const ALLOWED_ATTR = [
  'style', 'title', 'align', 'valign', 'width', 'height',
  'href', 'src', 'alt', 'border', 'cellpadding', 'cellspacing', 'colspan', 'rowspan',
];

/** Forbidden by name too — and their CONTENTS dropped, not unwrapped. */
const FORBID_TAGS = ['script', 'style', 'iframe', 'object', 'embed', 'form', 'input', 'button', 'select', 'textarea', 'option', 'link', 'base', 'meta', 'noscript', 'applet', 'frame', 'frameset'];

const purify = createDOMPurify(window);

/** The only per-call switch. DOMPurify hooks are instance-wide; sanitizing is synchronous, so a
 * module-level flag set just before `sanitize()` is race-free. */
let showImagesNow = false;

/** CSS `url(...)` references out of a `style` attribute value — always stripped. */
function stripCssUrls(style: string): string {
  return style.replace(/url\s*\([^)]*\)/gi, 'none');
}

purify.addHook('afterSanitizeAttributes', (node) => {
  if (node.nodeType !== 1) return;
  const el = node as Element;
  const tag = el.tagName.toLowerCase();
  const style = el.getAttribute('style');
  if (style !== null) el.setAttribute('style', stripCssUrls(style));
  if (tag === 'a') {
    // EVERY anchor is rewritten (the sandbox's allow-popups only works for links that open in a new
    // browsing context, and `<base>` is stripped everywhere — a base could retarget every relative
    // link — so the per-anchor rewrite, not a `<base target>`, is what sends links out): target and
    // rel are FORCED, whatever the sender wrote. Only http(s) and mailto survive; javascript:, data:,
    // vbscript:, file:, cid: and RELATIVE hrefs lose the anchor altogether (marked here, unwrapped to
    // plain text — link text kept — after serialization in `unwrapDeadLinks`).
    if (/^(?:https?:|mailto:)/i.test(el.getAttribute('href') ?? '')) {
      el.setAttribute('target', '_blank');
      el.setAttribute('rel', 'noopener noreferrer');
    } else {
      el.removeAttribute('href');
      el.removeAttribute('target');
      el.removeAttribute('rel');
      el.setAttribute('data-vowric-unlink', '1');
    }
  }
  if (tag === 'img') {
    const raw = el.getAttribute('src');
    el.removeAttribute('src');
    el.removeAttribute('srcset');
    if (raw && /^cid:/i.test(raw)) {
      el.setAttribute('data-placeholder', 'inline');
      el.setAttribute('alt', el.getAttribute('alt') || '(inline image)');
      return;
    }
    if (!raw || !/^https?:\/\//i.test(raw)) return; // nothing to hold or show
    el.setAttribute('data-blocked-src', raw);
    if (showImagesNow) el.setAttribute('src', raw);
    else el.setAttribute('data-placeholder', 'remote');
  }
});

const CONFIG = {
  ALLOWED_TAGS,
  ALLOWED_ATTR,
  FORBID_TAGS,
  FORBID_ATTR: ['srcset'],
  ADD_FORBID_CONTENTS: ['form', 'button', 'select', 'textarea', 'option'],
  ALLOW_DATA_ATTR: false,
  ALLOW_ARIA_ATTR: false,
  KEEP_CONTENT: true,
  ALLOWED_URI_REGEXP: /^(?:https?:|mailto:|cid:)/i,
};

/** Anchors the hook marked dead become plain text: the anchor goes, its text stays. Nested anchors
 * cannot exist in parsed HTML, so a non-greedy match to the next `</a>` is exact. */
function unwrapDeadLinks(html: string): string {
  return html.replace(/<a\b[^>]*\bdata-vowric-unlink="1"[^>]*>([\s\S]*?)<\/a>/g, '$1');
}

/** Belt before DOMPurify's own suspenders: the handful of DROP_WHOLE
 * tags that a browser (and happy-dom, this suite's own DOM) may act on
 * the MOMENT they are parsed — before DOMPurify ever gets to remove them —
 * never reach `DOMParser` at all. `<iframe>`/`<object>`/`<embed>` are the
 * load-bearing ones (a browser can start a navigation/fetch on parse,
 * independent of any attribute this file would otherwise strip);
 * `<script>`/`<style>` are included too since there is no reason to let a
 * parser build a node for either when DOMPurify removes them unconditionally
 * anyway. Matches across a reasonable span (DOTALL via `[\s\S]`) so a body
 * split across chunks still closes correctly; this is pre-parse hygiene,
 * not the sanitizer's only line of defence — DOMPurify still removes every one
 * of these tags again (and everything else) from the PARSED tree, so a
 * malformed/self-closing variant this regex missed is still caught there. */
function stripUnparsedDangerousTags(html: string): string {
  return html.replace(/<(script|style|iframe|object|embed)\b[^>]*>[\s\S]*?<\/\1\s*>/gi, '').replace(/<(iframe|embed)\b[^>]*\/?>/gi, '');
}

export function sanitizeEmailHtml(html: string, showImages: boolean): string {
  // Fail CLOSED: a DOM DOMPurify cannot run in yields no markup at all, never unsanitized markup.
  if (!purify.isSupported) return '';
  showImagesNow = showImages;
  try {
    return unwrapDeadLinks(String(purify.sanitize(stripUnparsedDangerousTags(html), CONFIG)));
  } finally {
    showImagesNow = false;
  }
}

/** The sandboxed page's own markup, with a strict CSP of its own (a second wall behind the
 * sandbox): `default-src 'none'` — no script, no frame, no connect, no form-action, no fonts, no
 * media — only inline style, and images from `data:`/`cid:`; "Show images" adds `https:` to img-src
 * and nothing else. There is deliberately NO `<base>` here (the sanitizer strips the mail's own and
 * we add none: a base could retarget every relative link) — links leave the sandbox through the
 * per-anchor `target="_blank" rel="noopener noreferrer"` rewrite instead. */
export function emailSrcdoc(sanitizedInnerHtml: string, showImages = false): string {
  const csp = `default-src 'none'; style-src 'unsafe-inline'; img-src data: cid:${showImages ? ' https:' : ''}`;
  return `<!DOCTYPE html><html><head><meta charset="utf-8"><meta http-equiv="Content-Security-Policy" content="${csp}"><style>
    html,body{margin:0;padding:0;color:#1a1a1a;background:#fff;font:14px/1.5 -apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif;word-wrap:break-word;overflow-wrap:anywhere;}
    body{padding:4px 2px;}
    img{max-width:100%;}
    table{max-width:100%;}
    [data-placeholder]{display:inline-block;min-width:48px;min-height:32px;background:#eceff1;border:1px dashed #b0bec5;color:#607d8b;font-size:11px;text-align:center;line-height:32px;border-radius:3px;}
    @media (prefers-color-scheme: dark){html,body{color:#e8e8e8;background:#1c1d1f;}[data-placeholder]{background:#2a2c2f;border-color:#44474a;color:#9aa0a6;}}
  </style></head><body>${sanitizedInnerHtml}</body></html>`;
}

/** Auto-height WITHOUT ever writing message markup into the app's own DOM (no innerHTML — only
 * `srcdoc`, always). The displayed iframe's sandbox (no allow-same-origin) makes its document
 * unreadable from here by construction, so a second, HIDDEN measuring iframe holds the SAME
 * sanitized+CSP'd srcdoc under `sandbox="allow-same-origin"` — NO allow-scripts, so nothing in it
 * can run; same-origin only lets THIS page read its laid-out height. Clamped [40, 4000] px: a
 * message that measures to 0 never collapses, one that measures absurdly tall scrolls in its own
 * frame instead of pushing the pane off-screen. */
export function useEmailFrameHeight(): { onMeasureLoad: (e: Event) => void; height: number } {
  const [height, setHeight] = useState(120);
  const onMeasureLoad = (e: Event) => {
    try {
      const doc = (e.currentTarget as HTMLIFrameElement).contentDocument;
      const raw = doc?.documentElement?.scrollHeight ?? 0;
      setHeight(Math.max(40, Math.min(4000, raw || 120)));
    } catch {
      /* unreadable: keep the default height */
    }
  };
  return { onMeasureLoad, height };
}
